import React, { useMemo, useState } from 'react';
import { Badge, Form, Nav, Spinner } from 'react-bootstrap';
import { EyeOff, HelpCircle, Image, Layers, PanelTop, RotateCcw, Search, Tag, Type } from 'lucide-react';
import { htmlToText, truncate } from './ui';
import SeoFindings from './SeoFindings';
import FaqCard from './FaqCard';
import { countBySeverity, liveFindings, severitySummary } from './seoRules';

const TITLE_LABELS = ['Title', 'Social title (og:title)', 'Twitter title'];
const DESCRIPTION_LABELS = ['Description', 'Social description (og:description)', 'Twitter description'];

/** Value currently shown for a unit: the pending edit, or its original. */
const useValueOf = (edits, originalOf) => (unit) => (edits.has(unit.id) ? edits.get(unit.id) : originalOf(unit));

const CharCount = ({ value, max }) => {
  const n = value.length;
  return (
    <span className={`mse-count${n > max ? ' over' : ''}`} title={`Recommended maximum ${max} characters`}>
      {n}/{max}
    </span>
  );
};

/** Title or description: one field when all three tags agree, separate fields when they differ. */
const MetaGroup = ({ label, units, max, valueOf, canEdit, onEdit, multiline }) => {
  if (units.length === 0) return null;
  const values = units.map(valueOf);
  const originals = units.map((u) => u.value);
  const unified = originals.every((v) => v === originals[0]);
  const Control = (props) =>
    multiline ? <Form.Control as="textarea" rows={3} {...props} /> : <Form.Control {...props} />;

  const ids = units.map((u) => u.id).join(' ');
  if (unified) {
    return (
      <Form.Group className="mb-3" data-unit-ids={ids}>
        <div className="mse-field-head">
          <Form.Label className="mb-0">{label}</Form.Label>
          {units.length > 1 && <span className="mse-hint">applies to {units.length} tags</span>}
          <CharCount value={values[0]} max={max} />
        </div>
        {Control({
          value: values[0],
          readOnly: !canEdit,
          onChange: (e) => units.forEach((u) => onEdit(u, e.target.value)),
        })}
      </Form.Group>
    );
  }
  return (
    <div className="mb-3">
      <div className="mse-field-head">
        <Form.Label className="mb-0">{label}</Form.Label>
        <span className="mse-hint">tags differ</span>
      </div>
      {units.map((u, i) => (
        <Form.Group key={u.id} className="mb-2" data-unit-ids={u.id}>
          <div className="mse-field-head">
            <span className="mse-sublabel">{u.label}</span>
            <CharCount value={values[i]} max={max} />
          </div>
          {Control({ value: values[i], readOnly: !canEdit, onChange: (e) => onEdit(u, e.target.value) })}
          {canEdit && i === 0 && (
            <button type="button" className="mse-link-button" onClick={() => units.forEach((o) => onEdit(o, values[0]))}>
              Use this for all {units.length}
            </button>
          )}
        </Form.Group>
      ))}
    </div>
  );
};

const JsonLdField = ({ unit, value, canEdit, onEdit }) => {
  let valid = true;
  try {
    JSON.parse(value);
  } catch {
    valid = false;
  }
  return (
    <Form.Group className="mb-3" data-unit-ids={unit.id}>
      <div className="mse-field-head">
        <span className="mse-sublabel">{unit.label}</span>
        {!valid && <Badge bg="danger">Invalid JSON</Badge>}
      </div>
      <Form.Control
        as="textarea"
        rows={8}
        className="mse-mono"
        spellCheck={false}
        value={value}
        readOnly={!canEdit}
        onChange={(e) => onEdit(unit, e.target.value)}
      />
    </Form.Group>
  );
};

const AltTextFields = ({ images, valueOf, canEdit, onEdit, onHighlight }) =>
  images.length > 0 && (
    <>
      <h6 className="mse-section-title">
        <Image size={14} /> Image alt text
      </h6>
      {images.map((u) => (
        <Form.Group key={u.id} className="mb-2" data-unit-ids={u.id}>
          <button type="button" className="mse-link-button mse-sublabel" onClick={() => onHighlight(u.id)}>
            {u.label.replace('Image alt text: ', '')}
          </button>
          <Form.Control
            size="sm"
            value={valueOf(u)}
            placeholder="Decorative (no alt text)"
            readOnly={!canEdit}
            onChange={(e) => onEdit(u, e.target.value)}
          />
        </Form.Group>
      ))}
    </>
  );

const pageName = (path) => (path === 'site/index.html' ? 'Home' : path.replace(/^site\//, '').replace(/\.html$/, ''));

/** Shared header/footer: where its text is used, and which pages would not take an edit. */
const SharedFieldsTab = ({ page, units, valueOf, canEdit, onEdit, onHighlight, onOpenPage }) => {
  const { name, variants, pagesWithout } = page.shared;
  const total = variants.reduce((n, v) => n + v.pages, 0) + pagesWithout;
  const matching = variants.find((v) => v.canonical)?.pages ?? 0;
  const others = variants.filter((v) => !v.canonical);
  return (
    <div className="mse-panel-body">
      <h6 className="mse-section-title">
        <Layers size={14} /> Where this {name} is used
      </h6>
      <p className="mb-2">
        {matching === total
          ? `The same ${name} text is on all ${total} pages.`
          : `${matching} of ${total} pages have exactly this ${name} text.`}
      </p>
      {others.length > 0 && (
        <>
          <p className="mse-hint">
            These pages have a different {name}. An edit reaches them only if they contain the text you changed;
            otherwise they are skipped and listed when you save.
          </p>
          <ul className="mse-variants">
            {others.map((v, i) => (
              <li key={i}>
                <strong>{v.pages} page{v.pages === 1 ? '' : 's'}</strong>
                <span className="mse-hint"> with {v.units} texts</span>
                <div className="mse-variant-pages">
                  {v.samplePaths.map((path) => (
                    <button key={path} type="button" className="mse-link-button" onClick={() => onOpenPage(path)}>
                      {pageName(path)}
                    </button>
                  ))}
                  {v.pages > v.samplePaths.length && <span className="mse-hint">and {v.pages - v.samplePaths.length} more</span>}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
      {pagesWithout > 0 && (
        <p className="mse-hint">
          {pagesWithout} page{pagesWithout === 1 ? ' has' : 's have'} no shared {name}.
        </p>
      )}
      <AltTextFields images={units.filter((u) => u.group === 'images')} valueOf={valueOf} canEdit={canEdit} onEdit={onEdit} onHighlight={onHighlight} />
      <p className="mse-hint mt-3">
        This is the home page&apos;s copy. Saving changes the same text on every page in one commit. Menus that open
        on click are listed under All text.
      </p>
    </div>
  );
};

const SeoCard = ({ units, valueOf, lint, onFocusUnit }) => {
  const [showNotes, setShowNotes] = useState(false);
  if (lint?.error) {
    return <p className="mse-hint">SEO check unavailable: {lint.error}</p>;
  }
  const findings = liveFindings(units, valueOf, lint?.findings);
  const counts = countBySeverity(findings);
  const shown = showNotes ? findings : findings.filter((f) => f.severity !== 'INFO');
  return (
    <div className="mse-seo-card">
      <div className="mse-field-head">
        <span className="mse-seo-summary">{severitySummary(counts)}</span>
        {lint?.loading && <Spinner animation="border" size="sm" className="ms-1" />}
        {counts.INFO > 0 && (
          <button type="button" className="mse-link-button ms-auto" onClick={() => setShowNotes((v) => !v)}>
            {showNotes ? 'Hide notes' : `Show ${counts.INFO} note${counts.INFO === 1 ? '' : 's'}`}
          </button>
        )}
      </div>
      {shown.length > 0 && <SeoFindings findings={shown} onFocusUnit={onFocusUnit} />}
    </div>
  );
};

const FieldsTab = ({ units, valueOf, canEdit, onEdit, onHighlight, lint, onFocusUnit, faq, faqProps }) => {
  const byLabel = (labels) => labels.map((l) => units.find((u) => u.group === 'head' && u.label === l)).filter(Boolean);
  const prices = units.filter((u) => u.group === 'prices');
  const images = units.filter((u) => u.group === 'images');
  const structured = units.filter((u) => u.group === 'structured');

  return (
    <div className="mse-panel-body">
      <h6 className="mse-section-title">
        <Search size={14} /> SEO
      </h6>
      <SeoCard units={units} valueOf={valueOf} lint={lint} onFocusUnit={onFocusUnit} />

      <h6 className="mse-section-title">
        <PanelTop size={14} /> Page metadata
      </h6>
      <MetaGroup label="Title" units={byLabel(TITLE_LABELS)} max={60} valueOf={valueOf} canEdit={canEdit} onEdit={onEdit} />
      <MetaGroup
        label="Description"
        units={byLabel(DESCRIPTION_LABELS)}
        max={160}
        valueOf={valueOf}
        canEdit={canEdit}
        onEdit={onEdit}
        multiline
      />

      {faq && (
        <>
          <h6 className="mse-section-title">
            <HelpCircle size={14} /> FAQ
          </h6>
          <FaqCard faq={faq} units={units} valueOf={valueOf} canEdit={canEdit} onEdit={onEdit} onHighlight={onHighlight} {...faqProps} />
        </>
      )}

      {prices.length > 0 && (
        <>
          <h6 className="mse-section-title">
            <Tag size={14} /> Prices by currency
          </h6>
          <p className="mse-hint">Shown when a visitor switches currency. The page text is the GBP price.</p>
          {prices.map((u) => (
            <Form.Group key={u.id} className="mb-2">
              <span className="mse-sublabel">{u.label}</span>
              <Form.Control size="sm" value={valueOf(u)} readOnly={!canEdit} onChange={(e) => onEdit(u, e.target.value)} />
            </Form.Group>
          ))}
        </>
      )}

      <AltTextFields images={images} valueOf={valueOf} canEdit={canEdit} onEdit={onEdit} onHighlight={onHighlight} />

      {structured.length > 0 && (
        <>
          <h6 className="mse-section-title">
            <Type size={14} /> Structured data
          </h6>
          <p className="mse-hint">
            JSON-LD read by search engines and AI assistants. FAQ edits above update it unless you edit it here by hand.
          </p>
          {structured.map((u) => (
            <JsonLdField key={u.id} unit={u} value={valueOf(u)} canEdit={canEdit} onEdit={onEdit} />
          ))}
        </>
      )}

      <p className="mse-hint mt-3">
        The site header and footer are shared by every page. Edit them under Shared in the sidebar.
      </p>
    </div>
  );
};

const AllTextTab = ({ units, valueOf, canEdit, bridge, activeUnitId, onEdit, canvasShown, inCanvas }) => {
  const [expanded, setExpanded] = useState(null);
  const bodyUnits = units.filter((u) => u.group === 'body');

  const sections = useMemo(() => {
    const result = [];
    for (const u of bodyUnits) {
      const name = u.section || 'Page';
      if (!result.length || result[result.length - 1].name !== name) result.push({ name, units: [] });
      result[result.length - 1].units.push(u);
    }
    return result;
  }, [bodyUnits]);

  const openUnit = (u, hidden) => {
    if (!hidden && bridge) {
      inCanvas(() => bridge.focus(u.id));
      setExpanded(null);
    } else {
      setExpanded(expanded === u.id ? null : u.id);
    }
  };

  return (
    <div className="mse-panel-body mse-outline">
      <p className="mse-hint">
        Every editable text on the page, in order. Text marked hidden only appears on the page after a click (tabs,
        menus); edit it here.
      </p>
      {sections.map((section, i) => (
        <div key={`${section.name}-${i}`} className="mse-outline-section">
          <div className="mse-outline-section-name">{section.name}</div>
          {section.units.map((u) => {
            // A preview in another pane has no layout to measure; the server knows what sits in closed <details>
            const hidden = bridge ? (canvasShown ? bridge.isHidden(u.id) : u.inFoldedContent) : false;
            const value = valueOf(u);
            return (
              <div key={u.id} className={`mse-outline-row${activeUnitId === u.id ? ' active' : ''}`}>
                <button type="button" className="mse-outline-summary" onClick={() => openUnit(u, hidden)}>
                  <span className="mse-tag">{u.tag}</span>
                  <span className="mse-outline-text">{truncate(u.kind === 'HTML' ? htmlToText(value) : value, 90) || '(empty)'}</span>
                  {hidden && <EyeOff size={13} className="mse-hidden-icon" aria-label="Hidden in preview" />}
                </button>
                {expanded === u.id && (
                  <Form.Control
                    as="textarea"
                    rows={u.kind === 'HTML' ? 4 : 2}
                    className={u.kind === 'HTML' ? 'mse-mono' : undefined}
                    value={value}
                    readOnly={!canEdit}
                    onChange={(e) => onEdit(u, e.target.value)}
                    autoFocus
                  />
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const ChangesTab = ({ units, edits, originalOf, canEdit, onRevert, onHighlight }) => {
  const changed = units.filter((u) => edits.has(u.id));
  if (changed.length === 0) {
    return <div className="mse-panel-body mse-empty">No unsaved changes. Click any text in the page to edit it.</div>;
  }
  const asText = (u, v) => (u.kind === 'HTML' ? htmlToText(v) : v);
  return (
    <div className="mse-panel-body">
      {changed.map((u) => (
        <div key={u.id} className="mse-change">
          <div className="mse-field-head">
            <button type="button" className="mse-link-button mse-sublabel" onClick={() => onHighlight(u.id)}>
              {u.group === 'body' ? `<${u.tag}>` : u.label}
            </button>
            {canEdit && (
              <button type="button" className="mse-link-button mse-revert" onClick={() => onRevert(u)}>
                <RotateCcw size={12} className="me-1" />
                Revert
              </button>
            )}
          </div>
          <div className="mse-before">{truncate(asText(u, originalOf(u)), 220) || '(empty)'}</div>
          <div className="mse-after">{truncate(asText(u, edits.get(u.id)), 220) || '(empty)'}</div>
        </div>
      ))}
    </div>
  );
};

const PageFieldsPanel = ({
  page,
  edits,
  originalOf,
  canEdit,
  bridge,
  activeUnitId,
  lint,
  faqProps,
  onEdit,
  onRevert,
  onHighlight,
  onOpenPage,
  canvasShown = true,
  inCanvas = (fn) => fn(),
}) => {
  const [tab, setTab] = useState('fields');
  const valueOf = useValueOf(edits, originalOf);
  const units = page?.units || [];

  /** From an SEO finding: text in the preview is focused there, head fields in this panel. */
  const focusUnit = (id) => {
    const unit = units.find((u) => u.id === id);
    if (unit?.inCanvas && unit.group === 'body' && bridge?.has(id)) {
      inCanvas(() => bridge.focus(id));
      return;
    }
    const field = document.querySelector(`.mse-panel [data-unit-ids~="${id}"]`);
    if (field) {
      field.scrollIntoView({ block: 'center', behavior: 'smooth' });
      field.querySelector('input, textarea')?.focus({ preventScroll: true });
    } else if (bridge?.has(id)) {
      inCanvas(() => bridge.highlight(id));
    }
  };

  return (
    <aside className="mse-panel">
      <Nav variant="tabs" activeKey={tab} onSelect={(k) => setTab(k)} className="mse-panel-tabs">
        <Nav.Item>
          <Nav.Link eventKey="fields">Fields</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="text">All text</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="changes">
            Changes{edits.size > 0 && <span className="mse-tab-count">{edits.size}</span>}
          </Nav.Link>
        </Nav.Item>
      </Nav>
      {!page && <div className="mse-panel-body mse-empty">Select a page.</div>}
      {page && tab === 'fields' && !page.shared && (
        <FieldsTab
          units={units}
          valueOf={valueOf}
          canEdit={canEdit}
          onEdit={onEdit}
          onHighlight={onHighlight}
          lint={lint}
          onFocusUnit={focusUnit}
          faq={page.faq}
          faqProps={faqProps}
        />
      )}
      {page && tab === 'fields' && page.shared && (
        <SharedFieldsTab
          page={page}
          units={units}
          valueOf={valueOf}
          canEdit={canEdit}
          onEdit={onEdit}
          onHighlight={onHighlight}
          onOpenPage={onOpenPage}
        />
      )}
      {page && tab === 'text' && (
        <AllTextTab
          units={units}
          valueOf={valueOf}
          canEdit={canEdit}
          bridge={bridge}
          activeUnitId={activeUnitId}
          onEdit={onEdit}
          canvasShown={canvasShown}
          inCanvas={inCanvas}
        />
      )}
      {page && tab === 'changes' && (
        <ChangesTab
          units={units}
          edits={edits}
          originalOf={originalOf}
          canEdit={canEdit}
          onRevert={onRevert}
          onHighlight={onHighlight}
        />
      )}
    </aside>
  );
};

export default PageFieldsPanel;
